// ═══════════════════════════════════════════════════
//  A channelled design with no channel width, named once
//
//  resolveChannelCount opens with `channel_width ?? 0; if (<= 0) return 0`.
//  Zero channels is never harmless, but WHAT it costs depends on the design:
//  a tufted channelled design loses three real costs, a channel-seam design
//  loses none and only reports a wrong count. One absent number, two different
//  consequences, and until this function said so it said nothing at all.
//
//  Same shape as button-hardware.ts, and the same reason for existing: there
//  are eight cost-helper blocks that are supposed to be numeric mirrors, and
//  they have drifted before. Call sites decide only where to put the warning,
//  never what it is.
// ═══════════════════════════════════════════════════

import type { UpholsteryDesignTraits } from './upholstery-design-traits';

/** The design sub-fields this reads. `any` elsewhere in the helpers.
 *  `channel_count_override` is listed but never read — see the docblock
 *  below — it is here only so a real design object (which carries it
 *  alongside `channel_width`) satisfies this type without a cast. */
export interface ChannelWidthDesign {
  type?: string | null;
  channel_width?: number | null;
  channel_count_override?: number | null;
}

/**
 * The warning for one component's channel width, or null when there is none.
 *
 * @param design  the parsed `config.design` block. Read defensively — a helper
 *                that hits a config with no design block must get a warning,
 *                not a throw that takes the whole price down.
 * @param traits  the resolved catalog traits, already in hand at every call
 *                site because resolveChannelCount needs them too. Passing them
 *                rather than re-resolving keeps this function free of the
 *                catalog-rows plumbing.
 *
 * **The message branches on `is_tufted`, and that split is the whole point.**
 * The three costs a missing width is famous for zeroing — the per-channel
 * fabric allowance (resolveDesignTypeAllowancePct), the tufting wire
 * (resolveWireLinearFeet) and the channel labor minutes (labor-cost.service's
 * vertical/horizontal channel counts) — are every one of them gated on
 * `is_tufted`, NOT on `has_channels`. For a channelled-but-untufted design —
 * `channel_seam`, and any admin-created design marked the same way — all three
 * are zero at any width, so a missing width costs exactly nothing and saying
 * otherwise would be a specific, checkable lie. An operator who verifies one
 * such warning and finds nothing was lost stops reading the next one, which is
 * the failure this warning exists to avoid.
 *
 * It still fires for those designs, quietly: `breakdown.channel_count_per_section`
 * and `breakdown.total_channels` are reported as zero when they are not, and
 * the raw count is passed to labor rate matching as `analysis.channel_count`,
 * where `min_channel_count` / `max_channel_count` can select a different rate
 * band. That is a wrong number reaching a price, just not through the three
 * lines the tufted message names.
 *
 * Both branches open with the same `0 channels priced` prefix. Four client
 * modal previews, the `note` that reaches Cria, and several tests match on it;
 * it is load-bearing and must stay identical in both.
 *
 * Deliberately says nothing about `channel_count_override`. That override is
 * checked AFTER the width gate inside resolveChannelCount, so an override with
 * no width is also zero channels — the warning is correct as written, and the
 * gate order is left alone because changing it would move prices.
 */
export function resolveChannelWidthWarning(
  design: ChannelWidthDesign | null | undefined,
  traits: UpholsteryDesignTraits,
): string | null {
  if (!traits?.has_channels) return null;

  const width = design?.channel_width ?? 0;
  if (width > 0) return null;

  const type = design?.type ?? 'this design';

  if (!traits.is_tufted) {
    return (
      `0 channels priced — "${type}" is a channelled design but no channel ` +
      `width is set, so the reported channel count is zero. This design is ` +
      `not tufted, so no allowance, labor or wire cost is lost by that.`
    );
  }

  return (
    `0 channels priced — "${type}" is a channelled design but no channel ` +
    `width is set, so the channel allowance, the channel labor and the ` +
    `tufting wire are all costing zero.`
  );
}
